import { motion } from 'framer-motion'
import { Activity, Bot, CheckCircle2, Clock, Cpu, Layers, LayoutDashboard, Settings, Users, Zap } from 'lucide-react'

export function GovernMockup() {
  return (
    <div className="flex h-full w-full flex-col overflow-hidden rounded-xl bg-surface-elevated text-text-primary shadow-2xl">
      {/* Top bar */}
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent">
            <Bot className="h-5 w-5 text-white" />
          </div>
          <div className="h-2.5 w-24 rounded bg-surface" />
        </div>
        <div className="flex items-center gap-3">
          <div className="h-2 w-16 rounded bg-surface" />
          <div className="h-8 w-8 rounded-full bg-surface" />
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <div className="hidden w-14 flex-col items-center gap-4 border-r border-border py-4 sm:flex">
          <LayoutDashboard className="h-5 w-5 text-accent-light" />
          <Bot className="h-5 w-5 text-text-muted" />
          <Layers className="h-5 w-5 text-text-muted" />
          <Users className="h-5 w-5 text-text-muted" />
          <Activity className="h-5 w-5 text-text-muted" />
          <Settings className="mt-auto h-5 w-5 text-text-muted" />
        </div>

        {/* Main content */}
        <div className="flex-1 overflow-auto p-4 sm:p-6">
          {/* Metrics */}
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <MetricCard label="Agentes activos" value="12" change="+3" icon={Bot} delay={0} />
            <MetricCard label="Ejecuciones hoy" value="1.2k" change="+18%" icon={Zap} delay={0.1} />
            <MetricCard label="Tasa de éxito" value="96.2%" change="+2.1%" icon={CheckCircle2} delay={0.2} />
            <MetricCard label="Tiempo ahorrado" value="48h" change="+12h" icon={Clock} delay={0.3} />
          </div>

          {/* Chart + recent runs */}
          <div className="mt-4 grid gap-4 lg:grid-cols-3">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="col-span-2 rounded-xl border border-border bg-surface p-4"
            >
              <div className="mb-4 flex items-center justify-between">
                <h4 className="text-sm font-medium text-text-primary">Rendimiento de agentes</h4>
                <Cpu className="h-4 w-4 text-text-muted" />
              </div>
              <div className="flex h-32 items-end gap-2">
                {[35, 55, 40, 70, 50, 85, 65, 90, 75, 95, 80, 100].map((h, i) => (
                  <motion.div
                    key={i}
                    initial={{ height: 0 }}
                    animate={{ height: `${h}%` }}
                    transition={{ delay: 0.5 + i * 0.05, duration: 0.5 }}
                    className="flex-1 rounded-t bg-gradient-to-t from-accent/40 to-accent-light"
                  />
                ))}
              </div>
              <div className="mt-2 flex justify-between text-xs text-text-muted">
                <span>00:00</span>
                <span>06:00</span>
                <span>12:00</span>
                <span>18:00</span>
                <span>24:00</span>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="rounded-xl border border-border bg-surface p-4"
            >
              <h4 className="mb-3 text-sm font-medium text-text-primary">Ejecuciones recientes</h4>
              <div className="space-y-3">
                <RunItem name="Qualify Lead" status="success" time="2m" />
                <RunItem name="Invoice Check" status="success" time="5m" />
                <RunItem name="Support Ticket" status="warning" time="8m" />
                <RunItem name="Data Sync" status="success" time="12m" />
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  )
}

function MetricCard({
  label,
  value,
  change,
  icon: Icon,
  delay,
}: {
  label: string
  value: string
  change: string
  icon: React.ElementType
  delay: number
}) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay }}
      className="rounded-xl border border-border bg-surface p-3"
    >
      <div className="flex items-center justify-between">
        <span className="text-xs text-text-muted">{label}</span>
        <Icon className="h-4 w-4 text-accent-light" />
      </div>
      <div className="mt-2 flex items-end gap-2">
        <span className="text-xl font-semibold text-text-primary">{value}</span>
        <span className="mb-0.5 text-xs text-emerald-400">{change}</span>
      </div>
    </motion.div>
  )
}

function RunItem({ name, status, time }: { name: string; status: 'success' | 'warning'; time: string }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <div className="flex items-center gap-2">
        <div className={`h-2 w-2 rounded-full ${status === 'success' ? 'bg-emerald-400' : 'bg-amber-400'}`} />
        <span className="text-text-secondary">{name}</span>
      </div>
      <span className="text-xs text-text-muted">{time}</span>
    </div>
  )
}
